'use client'

import { CheckIcon } from '@radix-ui/react-icons'
import { subscriptionPlanFragment } from '@vendure/hub/data-access'
import { formatCurrency } from '@vendure/hub/util'
import { readFragment } from '@vendure/hub/util-vendure-types'
import { Badge } from '@vendure/shadcn/ui'
import { cn } from '@vendure/shadcn/util'
import { Heading } from '@vendure/shared/ui'
import type { FragmentOf } from 'gql.tada'
import { useEffect, useMemo } from 'react'

export function SubscriptionPlanSelector({
  subscriptionPlans,
  onSelectionChange,
  selectedPlanId,
}: {
  selectedPlanId: string | undefined
  subscriptionPlans: Array<FragmentOf<typeof subscriptionPlanFragment>>
  onSelectionChange?: (planId: string) => void
}) {
  const plans = readFragment(subscriptionPlanFragment, subscriptionPlans)

  const monthlyPlan = useMemo(
    () => plans.find((plan) => plan.schedule.intervalUnit === 'month'),
    [plans],
  )

  const annualPlan = useMemo(
    () => plans.find((plan) => plan.schedule.intervalUnit === 'year'),
    [plans],
  )
  const annualPlanMonthlyPrice = useMemo(() => {
    return annualPlan ? annualPlan.recurringPrice / 12 : undefined
  }, [annualPlan])

  const annualPlanDiscountPercentage = useMemo(() => {
    return annualPlan && monthlyPlan
      ? (
          100 -
          (annualPlanMonthlyPrice! / monthlyPlan.recurringPrice) * 100
        ).toFixed(0)
      : undefined
  }, [annualPlanMonthlyPrice, monthlyPlan])

  useEffect(() => {
    const firstPlan = plans.at(0)
    if (firstPlan) {
      onSelectionChange?.(firstPlan.id)
    }
  }, [])

  return (
    <div className="flex flex-col gap-2">
      {plans.map((plan) => (
        <div
          onClick={() => {
            onSelectionChange?.(plan.id)
          }}
          key={plan.id}
          className={cn({
            'mb-2 flex cursor-pointer justify-between rounded-md border border-2 border-white/10 bg-slate-900 p-6 text-white/50 transition-all':
              true,
            'border-primary-400 text-white': selectedPlanId === plan.id,
          })}
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Heading className="text-[inherit]">
                {plan.schedule.intervalUnit === 'month'
                  ? 'Monthly'
                  : plan.schedule.intervalUnit === 'year'
                    ? 'Annually'
                    : plan.name}
              </Heading>
            </div>
            {plan.trialPeriodDays > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <CheckIcon /> {plan.trialPeriodDays} day free trial
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <CheckIcon /> Updates and support
            </div>
          </div>
          <div className="space-y-2 text-end">
            {plan === annualPlan && annualPlanDiscountPercentage && (
              <Badge variant="destructive">
                Save {annualPlanDiscountPercentage}%
              </Badge>
            )}
            <div>
              <div className="flex items-center justify-end gap-1">
                <div className="text-lg font-bold text-white">
                  {formatCurrency(
                    plan === annualPlan
                      ? annualPlanMonthlyPrice!
                      : plan.recurringPrice,
                    plan.currencyCode,
                  )}
                </div>
                <div>
                  <span className="text-sm text-white/60">/ month</span>
                </div>
              </div>
              {plan === annualPlan && (
                <div className="text-xs text-white/50">billed annually</div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
